import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { importarColaboradoresApi, previewImportacaoApi, type GgImportLinha } from "@/services/genteGestao";

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

export function ModalImportarColaboradores({ open, onClose, onImported }: Props) {
  const [texto, setTexto] = useState("");
  const [linhas, setLinhas] = useState<GgImportLinha[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  async function previewFromText(raw: string) {
    setLoading(true);
    setError(null);
    try {
      setLinhas(await previewImportacaoApi(raw));
    } catch {
      setError("Não foi possível validar a lista.");
    } finally {
      setLoading(false);
    }
  }

  function onFile(file: File | null) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result ?? "");
      setTexto(raw);
      void previewFromText(raw);
    };
    reader.readAsText(file);
  }

  async function confirmar() {
    if (!linhas) return;
    setSaving(true);
    setError(null);
    try {
      await importarColaboradoresApi(linhas.filter((l) => !l.erro));
      onImported();
      onClose();
      setTexto("");
      setLinhas(null);
    } catch {
      setError("Não foi possível importar.");
    } finally {
      setSaving(false);
    }
  }

  const validas = linhas?.filter((l) => !l.erro).length ?? 0;
  const comErro = linhas?.filter((l) => l.erro).length ?? 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Importar colaboradores"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void confirmar()} loading={saving} disabled={!linhas || validas === 0}>
            Confirmar {validas > 0 ? `(${validas})` : ""}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-slate-600">
          Uma pessoa por linha, formato{" "}
          <code className="text-xs">Nome;usuario;setor;telefone;liderUsuario</code>. Só o nome é obrigatório. A
          última coluna é o usuário do líder direto (opcional).
        </p>
        <textarea
          className="min-h-[120px] w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
          placeholder={"Maria Silva;maria.silva;Engenharia;5511999999999;joao.souza"}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => void previewFromText(texto)} loading={loading}>
            Pré-visualizar
          </Button>
          <label className="cursor-pointer text-sm font-medium text-brand-700 hover:underline">
            Enviar CSV
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {linhas && (
          <div className="max-h-72 overflow-auto rounded-lg border border-slate-200">
            <p className="px-3 py-2 text-xs text-slate-500">
              {validas} válida(s) · {comErro} com erro
            </p>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Nome</th>
                  <th className="px-3 py-2">Usuário</th>
                  <th className="px-3 py-2">Setor</th>
                  <th className="px-3 py-2">Líder</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={l.linha} className={l.erro ? "bg-red-50 text-red-700" : ""}>
                    <td className="px-3 py-1.5">{l.nome || "—"}</td>
                    <td className="px-3 py-1.5">{l.usuario || "—"}</td>
                    <td className="px-3 py-1.5">{l.setor || "—"}</td>
                    <td className="px-3 py-1.5">{l.liderUsuario || "—"}</td>
                    <td className="px-3 py-1.5 text-xs">
                      {l.erro ?? (l.duplicado ? "Duplicado" : "OK")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
